import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { isNativePlatform } from '@/config/platform';
import {
  LayoutDashboard,
  Receipt,
  ShoppingCart,
  FileText,
  Landmark,
  Calendar,
  Heart,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Crown,
  Wallet,
  PiggyBank,
  Users
} from 'lucide-react';
import { Church } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/reservations', icon: PiggyBank, label: 'Reserveringen' },
  { path: '/transactions', icon: Receipt, label: 'Transacties' },
  { path: '/groceries', icon: ShoppingCart, label: 'Boodschappen' },
  { path: '/fixed-costs', icon: Landmark, label: 'Vaste Lasten' },
  { path: '/contracts', icon: FileText, label: 'Contracten' },
  { path: '/life-planning', icon: Calendar, label: 'Levensplanning' },
  { path: '/health', icon: Heart, label: 'Gezondheid' },
  { path: '/church', icon: Church, label: 'Kerkadministratie', churchOnly: true },
  { path: '/church-reports', icon: FileText, label: 'Rapportages', churchOnly: true },
  { path: '/church-relations', icon: Users, label: 'Relaties', churchOnly: true },
  { path: '/settings', icon: Settings, label: 'Instellingen' },
];

export const Sidebar = ({ isOpen, setIsOpen }) => {
  const { user, logout, upgradeToPremium } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [churchMode, setChurchMode] = useState(() => localStorage.getItem('app_profile') === 'church');
  useEffect(() => {
    const sync = () => setChurchMode(localStorage.getItem('app_profile') === 'church');
    window.addEventListener('app-profile-changed', sync);
    return () => window.removeEventListener('app-profile-changed', sync);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 1024) {
      setIsOpen(false);
    }
  }, [location.pathname, setIsOpen]);

  const handleLogout = () => {
    logout();
    navigate(isNativePlatform() ? '/auth' : '/');
  };

  const handleUpgrade = async () => {
    await upgradeToPremium();
    setPremiumOpen(false);
  };

  return (
    <aside
      data-testid="sidebar"
      className={`liquid-glass-surface fixed left-0 top-0 bottom-0 z-50 h-auto max-w-[calc(100vw-1.5rem)] border-r border-white/60 sidebar-transition transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0 w-72' : '-translate-x-full w-72 lg:translate-x-0 lg:w-20'
      }`}
    >
      <div className="ios-safe-top ios-safe-bottom flex h-full flex-col overflow-hidden">
        {/* Logo */}
        <div className="border-b border-border px-5 pb-4 pt-3 lg:p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Wallet className="w-5 h-5 text-primary-foreground" />
            </div>
            {isOpen && (
              <div className="animate-fade-in">
                <h1 className="font-heading font-bold text-lg text-foreground">budgettoppers</h1>
                <p className="text-xs text-muted-foreground">Financieel overzicht</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {navItems.filter((item) => churchMode ? ['Dashboard', 'Transacties', 'Kerkadministratie', 'Rapportages', 'Contracten', 'Relaties', 'Instellingen'].includes(item.label) : !item.churchOnly).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {isOpen && <span className="font-medium">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Premium upgrade */}
        {!user?.is_premium && isOpen && (
          <div className="p-4">
            <Dialog open={premiumOpen} onOpenChange={setPremiumOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="upgrade-premium-btn"
                  className="w-full p-4 rounded-xl bg-gradient-to-r from-secondary/20 to-secondary/10 border border-secondary/30 hover:border-secondary/50 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <Crown className="w-5 h-5 text-secondary" />
                    <div className="text-left">
                      <p className="font-semibold text-sm">Upgrade naar Premium</p>
                      <p className="text-xs text-muted-foreground">€3,99/maand</p>
                    </div>
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent data-testid="premium-modal">
                <DialogHeader>
                  <DialogTitle className="font-heading flex items-center gap-2">
                    <Crown className="w-6 h-6 text-secondary" />
                    budgettoppers Premium
                  </DialogTitle>
                  <DialogDescription>
                    Krijg meer overzicht en extra hulp in de app
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary text-sm">✓</span>
                      </div>
                      <span>Meer rekeningen toevoegen</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary text-sm">✓</span>
                      </div>
                      <span>Extra hulp bij energie en internet</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary text-sm">✓</span>
                      </div>
                      <span>Bijlagen bij contracten bewaren</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary text-sm">✓</span>
                      </div>
                      <span>Meer inzicht in wat-als situaties</span>
                    </li>
                  </ul>
                  <div className="pt-4 border-t">
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-3xl font-bold font-heading">€3,99</span>
                      <span className="text-muted-foreground">/maand</span>
                    </div>
                    <Button
                      data-testid="confirm-upgrade-btn"
                      onClick={handleUpgrade}
                      className="w-full rounded-full bg-primary hover:bg-primary/90"
                    >
                      Kies Premium
                    </Button>
                    <p className="text-xs text-center text-muted-foreground mt-2">
                      Of €29,99 per jaar
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* User section */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="font-semibold text-primary">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            {isOpen && (
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            )}
          </div>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            onClick={handleLogout}
            className={`w-full text-muted-foreground hover:text-destructive ${
              isOpen ? 'justify-start' : 'justify-center px-0'
            }`}
            aria-label="Uitloggen"
            title="Uitloggen"
          >
            <LogOut className={`h-4 w-4 ${isOpen ? 'mr-2' : ''}`} />
            {isOpen && 'Uitloggen'}
          </Button>
        </div>

        {/* Toggle button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="absolute -right-3 top-20 hidden h-6 w-6 items-center justify-center rounded-full border border-border bg-white shadow-md transition-colors hover:bg-muted lg:flex"
        >
          {isOpen ? (
            <ChevronLeft className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
